import { Injectable, Signal, WritableSignal, computed, inject, signal } from "@angular/core";
import { getLoggerFor } from "@tylertech/logging";
import { ToastService } from "./toast.service";
import { FeatureFlagHelperService, FeatureFlags } from "./featureFlagHelperService";

export enum BrowserNotificationKeys
{
    SMARTPULSE_NOTIFICATION_KEY = "allow-smartpulse-notifications"
}

@Injectable()
export class BrowserNotificationService
{
    private readonly log = getLoggerFor("BrowserNotificationService");
    private readonly $toastService = inject(ToastService);
    private readonly $featureFlagHelperService = inject(FeatureFlagHelperService);
    private readonly isBrowserNotificationFlagEnabled = this.$featureFlagHelperService.get(FeatureFlags.Shared_All_BrowserNotifications);
    protected readonly notificationCounts = new Map<BrowserNotificationKeys, WritableSignal<number>>();
    private readonly pendingToastMessages: string[] = [];
    private isPlayingToasts = false;


    //I currently expect to only support actual browsers and not mobile devices.
    //If we ever swap, we will need to change the notifications if we expect mobile.
    public get doesBrowserNotSupportNotifications()
    {
        return !("Notification" in window);
    }

    constructor()
    {
        for (const key of Object.values(BrowserNotificationKeys))
        {
            const currentCount = window.localStorage.getItem(this.getNotificationCountKey(key)) ?? 0;
            const numberToStore = Number.isSafeInteger(Number(currentCount)) ? Number(currentCount) : 0;
            window.localStorage.setItem(this.getNotificationCountKey(key), numberToStore.toString());
            this.notificationCounts.set(key, signal(numberToStore));
        }
    }

    public checkOrGetBrowserPermission(): Promise<boolean>
    {
        const currentPermission = this.getCurrentPermission();
        if (currentPermission === null)
        {
            return Notification.requestPermission()
                .then(result => result === "granted")
                .catch(error =>
                {
                    this.log.error(error, "An error occurred getting browser permission");
                    return false;
                });
        }
        else 
        {
            return Promise.resolve(currentPermission);
        }
    }

    public getCurrentPermission(): boolean | null
    {
        if (!this.isBrowserNotificationFlagEnabled())
            return false;

        if (this.doesBrowserNotSupportNotifications)
            return false;
        else if (Notification.permission === "granted")
            return true;
        else if (Notification.permission === "denied")
            return false;

        return null;
    }

    public checkIssuesForNotifications(): void
    {
        if (this.doesBrowserNotSupportNotifications)
        {
            this.$toastService
                .showToast("Browsers notifications are not supported.  Alerts in the app will be shown instead.", 10000, "error");
            return;
        }

        this.checkOrGetBrowserPermission().then((result) =>
        {
            if (!result)
                this.$toastService
                    .showToast("Browser notifications are turned off.  To allow browser notifications, please turn on in your browser settings.", 10000, "error");
            return;
        }
        );
    }

    private getNotificationCountKey(key: BrowserNotificationKeys): string
    {
        return `${key}-count`;
    }

    public getNotificationCount(key: BrowserNotificationKeys): Signal<number>
    {
        //Note that we should always expect a signal since the constructor populates all keys.
        const count = this.notificationCounts.get(key)!.asReadonly();
        return computed(() => this.isBrowserNotificationFlagEnabled() ? count() : 0);
    }

    public incrementNotificationCountForKey(key: BrowserNotificationKeys): void
    {
        if (!this.isBrowserNotificationFlagEnabled())
        {
            this.log.debug("Notification flag set to false. Not updating notification count.");
            return;
        }

        const currentCount = window.localStorage.getItem(this.getNotificationCountKey(key)) ?? 0;
        const updatedCount = Number.isSafeInteger(Number(currentCount)) ? Number(currentCount) + 1 : 1;
        window.localStorage.setItem(this.getNotificationCountKey(key), updatedCount.toString());
        this.notificationCounts.get(key)!.set(updatedCount);
    }

    public clearNotificationCountForKey(key: BrowserNotificationKeys): void
    {
        if (!this.isBrowserNotificationFlagEnabled())
        {
            this.log.debug("Notification flag set to false. Not clearing notification count.");
            return;
        }

        window.localStorage.setItem(this.getNotificationCountKey(key), "0");
        this.notificationCounts.get(key)!.set(0);
    }


    public sendNotification(title: string, message: string, showOnTab: boolean | Promise<boolean>, key?: BrowserNotificationKeys): void
    {
        if (!this.isBrowserNotificationFlagEnabled())
        {
            this.log.debug("Notification flag set to false. Exiting.");
            return;
        }
        if (key)
        {
            const result = window.localStorage.getItem(key);
            if (result !== "yes")
            {
                this.log.debug(`Key ${key} was not true.  Exiting.`);
                return;
            }
        }

        try
        {
            //For hidden documents, we likely should always just send the toast in the hopes they tab back.
            if (document.hidden)
            {
                if (this.doesBrowserNotSupportNotifications)
                {
                    this.log.debug("Browser does not support notifications.  running fallback.");
                    this.sendNonBrowserNotification(message, true);
                    return;
                }

                this.checkOrGetBrowserPermission()
                    .then((result) =>
                    {
                        if (result)
                            this.sendBrowserNotification(title, message);
                        else
                        {
                            this.log.debug("Notifications disabled.  running fallback.");
                            this.sendNonBrowserNotification(message, true);
                        }
                    })
                    .catch(error => 
                    {
                        this.log.error(error, "An error occurred sending browser notification; running fallback.");
                        this.sendNonBrowserNotification(message, true);
                    });
            }
            else
            {
                this.sendNonBrowserNotification(message, showOnTab);
            }
        }
        catch (error)
        {
            this.log.error(error, "An error occurred when sending a notification.");
        }
    }

    private sendBrowserNotification(title: string, message: string)
    {
        const notificationOptions = {
            silent: false,
            body: message,
            icon: "/library/tylerui/images/tylerLogo.png"
        };
        const notificationToSend = new Notification(title, notificationOptions);
        notificationToSend.onclick = (event =>
        {
            this.log.debug("Notification has been clicked: ", event);
            window.focus();
            notificationToSend.close();
        });

        this.log.debug("Browser notification sent.");
    }

    private sendNonBrowserNotification(message: string, showOnTab: boolean | Promise<boolean>): void
    {
        Promise.resolve(showOnTab)
            .then(shouldShowOnTab =>
            {
                if (!shouldShowOnTab)
                {
                    this.log.debug("Notification marked to not show.");
                    return;
                }
                //To my understanding, we do not want to have multiple messages at once.  Thus, rather than just immediately calling them, we will have a list of notifications and loop.
                this.pendingToastMessages.push(message);

                if (!this.isPlayingToasts)
                {
                    this.isPlayingToasts = true;
                    this.playNextToast();
                }
            })
            .catch(error => this.log.error(error, "Unable to determine tab"));
    }

    private playNextToast(): void
    {
        const toastDisplayMilliseconds = 6000;

        if (this.pendingToastMessages.length === 0)
        {
            this.isPlayingToasts = false;
            return;
        }

        const message = this.pendingToastMessages.shift()!;
        try
        {
            this.$toastService.showToast(message, toastDisplayMilliseconds);
            this.log.debug("Notification toast shown.");
        }
        catch (error)
        {
            this.log.error(error, "An error occurred showing a queued notification toast.");
        }
        finally
        {
            //The thought is we send one notification then wait a specific time before checking.
            //the toast should ONLY be called from a notification when the guard isn't active, so this should be fine.
            setTimeout(() => this.playNextToast(), toastDisplayMilliseconds);
        }
    }
}